import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Checkbox, TextField, Tooltip,
  Switch, FormControlLabel, Chip, Button
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BadgeIcon from '@mui/icons-material/Badge';
import ScheduleIcon from '@mui/icons-material/Schedule';
import Autocomplete from '@mui/material/Autocomplete';
import axios from 'axios';

const DIAS_SEMANA = [
  { value: 1, label: 'L' }, { value: 2, label: 'M' }, { value: 3, label: 'X' },
  { value: 4, label: 'J' }, { value: 5, label: 'V' }, { value: 6, label: 'S' }, { value: 7, label: 'D' },
];
const HORARIO_DEFAULT = { activo: 0, dias_permitidos: '1,2,3,4,5,6', hora_inicio: '08:00:00', hora_fin: '20:00:00' };

const esSuperAdmin = (() => {
  try { return JSON.parse(localStorage.getItem('usuario') || 'null')?.es_superadmin === 1; }
  catch { return false; }
})();

export default function PermisosUsuario() {
  const [usuarios, setUsuarios] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [series, setSeries] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [usuarioId, setUsuarioId] = useState('');
  const [sucursalesUsuario, setSucursalesUsuario] = useState([]);
  const [seriesUsuario, setSeriesUsuario] = useState([]);
  const [modulosUsuario, setModulosUsuario] = useState([]);
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState('');
  const [empresasContables, setEmpresasContables] = useState([]);
  const [empresasUsuario, setEmpresasUsuario] = useState([]);
  const [empresasRrhh, setEmpresasRrhh] = useState([]);
  const [empresasRrhhUsuario, setEmpresasRrhhUsuario] = useState([]);
  const [horario, setHorario] = useState(HORARIO_DEFAULT);
  const [horarioGuardando, setHorarioGuardando] = useState(false);
  const [horarioGuardado, setHorarioGuardado] = useState(false);

  useEffect(() => {
    axios.get('/api/usuarios').then(res => setUsuarios(res.data));
    axios.get('/api/sucursales').then(res => setSucursales(res.data));
    axios.get('/api/usuario-modulo/catalogo').then(res => setModulos(res.data));
    if (esSuperAdmin) {
      axios.get('/api/contabilidad/empresas').then(res => setEmpresasContables(Array.isArray(res.data) ? res.data : [])).catch(() => {});
      axios.get('/api/rrhh/empresas').then(res => setEmpresasRrhh(Array.isArray(res.data) ? res.data : [])).catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (usuarioId) {
      axios.get(`/api/usuario-sucursal/${usuarioId}`).then(res => setSucursalesUsuario(res.data.map(s => s.id)));
      axios.get(`/api/usuario-serie/${usuarioId}`).then(res => setSeriesUsuario(res.data.map(se => se.id)));
      axios.get(`/api/usuario-modulo/${usuarioId}`).then(res => setModulosUsuario(res.data));
      if (esSuperAdmin) {
        axios.get(`/api/contabilidad/usuarios-empresa/${usuarioId}`).then(res => setEmpresasUsuario(Array.isArray(res.data) ? res.data : [])).catch(() => setEmpresasUsuario([]));
        axios.get(`/api/rrhh/usuarios-empresa/${usuarioId}`).then(res => setEmpresasRrhhUsuario(Array.isArray(res.data) ? res.data : [])).catch(() => setEmpresasRrhhUsuario([]));
        axios.get(`/api/usuario-horario/${usuarioId}`).then(res => setHorario(res.data || HORARIO_DEFAULT)).catch(() => setHorario(HORARIO_DEFAULT));
      }
    } else {
      setSucursalesUsuario([]);
      setSeriesUsuario([]);
      setModulosUsuario([]);
      setEmpresasUsuario([]);
      setHorario(HORARIO_DEFAULT);
    }
  }, [usuarioId]);

  useEffect(() => {
    if (sucursalSeleccionada) {
      axios.get(`/api/series/sucursal/${sucursalSeleccionada}`).then(res => setSeries(res.data));
    } else {
      setSeries([]);
    }
  }, [sucursalSeleccionada]);

  const handleSucursalCheck = async (id_sucursal, checked) => {
    if (!usuarioId) return;
    if (checked) {
      await axios.post('/api/usuario-sucursal/asignar', { id_usuario: usuarioId, id_sucursal });
    } else {
      await axios.post('/api/usuario-sucursal/quitar', { id_usuario: usuarioId, id_sucursal });
    }
    axios.get(`/api/usuario-sucursal/${usuarioId}`).then(res => setSucursalesUsuario(res.data.map(s => s.id)));
  };

  const handleSerieCheck = async (id_serie, checked) => {
    if (!usuarioId) return;
    if (checked) {
      await axios.post('/api/usuario-serie/asignar', { id_usuario: usuarioId, id_serie });
    } else {
      await axios.post('/api/usuario-serie/quitar', { id_usuario: usuarioId, id_serie });
    }
    axios.get(`/api/usuario-serie/${usuarioId}`).then(res => setSeriesUsuario(res.data.map(se => se.id)));
  };

  const handleModuloCheck = async (modulo_codigo, checked) => {
    if (!usuarioId) return;
    if (checked) {
      await axios.post('/api/usuario-modulo/asignar', { usuario_id: usuarioId, modulo_codigo });
    } else {
      await axios.post('/api/usuario-modulo/quitar', { usuario_id: usuarioId, modulo_codigo });
    }
    axios.get(`/api/usuario-modulo/${usuarioId}`).then(res => setModulosUsuario(res.data));
  };

  const handleEmpresaContableCheck = async (empresa_id, checked) => {
    if (!usuarioId) return;
    if (checked) {
      await axios.post('/api/contabilidad/usuarios-empresa', { usuario_id: usuarioId, empresa_id });
    } else {
      await axios.delete(`/api/contabilidad/usuarios-empresa/${usuarioId}/${empresa_id}`);
    }
    axios.get(`/api/contabilidad/usuarios-empresa/${usuarioId}`).then(res => setEmpresasUsuario(Array.isArray(res.data) ? res.data : [])).catch(() => {});
  };

  const handleEmpresaRrhhCheck = async (empresa_id, checked) => {
    if (!usuarioId) return;
    if (checked) {
      await axios.post('/api/rrhh/usuarios-empresa', { usuario_id: usuarioId, empresa_id });
    } else {
      await axios.delete(`/api/rrhh/usuarios-empresa/${usuarioId}/${empresa_id}`);
    }
    axios.get(`/api/rrhh/usuarios-empresa/${usuarioId}`).then(res => setEmpresasRrhhUsuario(Array.isArray(res.data) ? res.data : [])).catch(() => {});
  };

  const diasHorarioArray = String(horario.dias_permitidos || '').split(',').map(s => Number(s.trim())).filter(Boolean);

  const toggleDiaHorario = (dia) => {
    const yaEsta = diasHorarioArray.includes(dia);
    const nuevos = yaEsta ? diasHorarioArray.filter(d => d !== dia) : [...diasHorarioArray, dia];
    setHorario(h => ({ ...h, dias_permitidos: nuevos.sort((a, b) => a - b).join(',') }));
  };

  const guardarHorario = async () => {
    if (!usuarioId) return;
    setHorarioGuardando(true);
    setHorarioGuardado(false);
    try {
      await axios.post('/api/usuario-horario', {
        usuario_id: usuarioId,
        activo: horario.activo ? 1 : 0,
        dias_permitidos: horario.dias_permitidos,
        hora_inicio: horario.hora_inicio,
        hora_fin: horario.hora_fin,
      });
      setHorarioGuardado(true);
      setTimeout(() => setHorarioGuardado(false), 2500);
    } finally {
      setHorarioGuardando(false);
    }
  };

  const usuarioSeleccionado = usuarios.find(u => u.id === usuarioId) || null;
  const esAdmin = usuarioSeleccionado?.perfil_id === 1;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1, mb: 4, width: '100%' }}>
      <Box sx={{ width: { xs: '100%', sm: '95%', md: '90%' }, maxWidth: 1200 }}>
        <Typography variant="h4" gutterBottom sx={{ textAlign: { xs: 'center', md: 'left' }, mb: 3, fontWeight: 'bold', color: '#222' }}>
          Permisos de Usuario
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2, alignItems: 'center' }}>
          <Autocomplete
            options={usuarios}
            getOptionLabel={option => option?.username || ''}
            value={usuarioSeleccionado}
            onChange={(_e, value) => setUsuarioId(value ? value.id : '')}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={params => (
              <TextField {...params} label="Usuario" margin="dense" sx={{ minWidth: 250 }} />
            )}
            sx={{ minWidth: 250 }}
          />
        </Box>
        {usuarioId && (
          <>
            {/* ── MÓDULOS ── */}
            <Paper sx={{ p: 3, borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', mb: 4 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, color: '#1976d2' }}>
                Módulos habilitados
              </Typography>
              {esAdmin ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Este usuario es <strong>Administrador</strong> — tiene acceso a todos los módulos por defecto.
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Selecciona los módulos del sistema a los que este usuario puede acceder.
                </Typography>
              )}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ py: 0.5 }}>Módulo</TableCell>
                      <TableCell align="center" sx={{ py: 0.5 }}>Acceso habilitado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {modulos.map(m => {
                      const bloqueado = m.solo_superadmin === 1 && !esSuperAdmin;
                      const checkbox = (
                        <Checkbox
                          checked={esAdmin || modulosUsuario.includes(m.codigo)}
                          disabled={esAdmin || bloqueado}
                          onChange={e => handleModuloCheck(m.codigo, e.target.checked)}
                          color="primary"
                          size="small"
                        />
                      );
                      return (
                        <TableRow key={m.codigo} sx={{ height: 36, opacity: bloqueado ? 0.5 : 1 }}>
                          <TableCell sx={{ py: 0.5 }}>
                            {m.nombre}
                            {bloqueado && (
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                (solo administrador del sistema)
                              </Typography>
                            )}
                            {!bloqueado && m.opt_out_default === 1 && (
                              <Tooltip title="Este módulo viene habilitado para todos los usuarios por defecto; desmarca la casilla solo para el usuario que quieras restringir">
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                  (activado por defecto)
                                </Typography>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.5 }}>
                            {bloqueado
                              ? <Tooltip title="Solo el administrador del sistema puede habilitar Contabilidad">{checkbox}</Tooltip>
                              : checkbox
                            }
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* ── HORARIO DE ACCESO (solo superadmin ve y gestiona) ── */}
            {esSuperAdmin && (
              <Paper sx={{ p: 3, borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <ScheduleIcon sx={{ color: '#1976d2' }} />
                  <Typography variant="h6" fontWeight="bold" sx={{ color: '#1976d2' }}>
                    Horario de acceso
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Por defecto los usuarios pueden ingresar en cualquier momento. Activa esta restricción
                  solo para los usuarios a los que quieras limitar el acceso a un horario y días específicos
                  — fuera de ese rango no podrán iniciar sesión, y si ya estaban conectados, se les
                  cerrará la sesión en su siguiente acción dentro del sistema.
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!horario.activo}
                      onChange={e => setHorario(h => ({ ...h, activo: e.target.checked ? 1 : 0 }))}
                      color="primary"
                    />
                  }
                  label={horario.activo ? 'Restricción de horario activada' : 'Sin restricción (acceso 24/7)'}
                />
                {!!horario.activo && (
                  <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Días permitidos
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.75 }}>
                        {DIAS_SEMANA.map(d => (
                          <Chip
                            key={d.value}
                            label={d.label}
                            color={diasHorarioArray.includes(d.value) ? 'primary' : 'default'}
                            variant={diasHorarioArray.includes(d.value) ? 'filled' : 'outlined'}
                            onClick={() => toggleDiaHorario(d.value)}
                            sx={{ cursor: 'pointer', fontWeight: 'bold', width: 40 }}
                          />
                        ))}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <TextField
                        label="Hora inicio"
                        type="time"
                        size="small"
                        value={String(horario.hora_inicio || '08:00:00').slice(0, 5)}
                        onChange={e => setHorario(h => ({ ...h, hora_inicio: `${e.target.value}:00` }))}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 170 }}
                      />
                      <Typography color="text.secondary">a</Typography>
                      <TextField
                        label="Hora fin"
                        type="time"
                        size="small"
                        value={String(horario.hora_fin || '20:00:00').slice(0, 5)}
                        onChange={e => setHorario(h => ({ ...h, hora_fin: `${e.target.value}:00` }))}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 170 }}
                      />
                    </Box>
                  </Box>
                )}
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Button variant="contained" onClick={guardarHorario} disabled={horarioGuardando}>
                    {horarioGuardando ? 'Guardando...' : 'Guardar horario'}
                  </Button>
                  {horarioGuardado && (
                    <Typography variant="body2" color="success.main" fontWeight="bold">✓ Guardado</Typography>
                  )}
                </Box>
              </Paper>
            )}

            {/* ── SUCURSALES ── */}
            <Paper sx={{ p: 3, borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', mb: 4 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#1976d2' }}>Sucursales habilitadas</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center" sx={{ py: 0.5 }}>Sucursal</TableCell>
                      <TableCell align="center" sx={{ py: 0.5 }}>Permitir acceso</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sucursales.map(s => (
                      <TableRow key={s.id} sx={{ height: 36 }}>
                        <TableCell align="center" sx={{ py: 0.5 }}>{s.nombre}</TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Checkbox checked={sucursalesUsuario.includes(s.id)} onChange={e => handleSucursalCheck(s.id, e.target.checked)} color="primary" size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* ── SERIES ── */}
            <Paper sx={{ p: 3, borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', mb: 4 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#1976d2' }}>Series por sucursal</Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                <Autocomplete
                  options={sucursales}
                  getOptionLabel={option => option?.nombre || ''}
                  value={sucursales.find(s => s.id === sucursalSeleccionada) || null}
                  onChange={(_e, value) => setSucursalSeleccionada(value ? value.id : '')}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={params => (
                    <TextField {...params} label="Sucursal" margin="dense" sx={{ minWidth: 250 }} />
                  )}
                  sx={{ minWidth: 250 }}
                />
              </Box>
              {sucursalSeleccionada && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell align="center" sx={{ py: 0.5 }}>Tipo Documento</TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>Serie</TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>Permitir uso</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {series.map(se => (
                        <TableRow key={se.id} sx={{ height: 36 }}>
                          <TableCell align="center" sx={{ py: 0.5 }}>{se.tipo_documento}</TableCell>
                          <TableCell align="center" sx={{ py: 0.5 }}>{se.serie}</TableCell>
                          <TableCell align="center" sx={{ py: 0.5 }}>
                            <Checkbox checked={seriesUsuario.includes(se.id)} onChange={e => handleSerieCheck(se.id, e.target.checked)} color="primary" size="small" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>

            {/* ── EMPRESAS RRHH (solo superadmin ve y gestiona) ── */}
            {esSuperAdmin && empresasRrhh.length > 0 && (
              <Paper sx={{ p: 3, borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <BadgeIcon sx={{ color: '#7b1fa2' }} />
                  <Typography variant="h6" fontWeight="bold" sx={{ color: '#7b1fa2' }}>
                    Empresas RR.HH.
                  </Typography>
                </Box>
                {esAdmin ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Este usuario es <strong>Administrador</strong> — tiene acceso a todas las empresas de RR.HH.
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Selecciona a qué empresas de RR.HH. puede acceder este usuario.
                  </Typography>
                )}
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ py: 0.5, fontWeight: 'bold' }}>Empresa RR.HH.</TableCell>
                        <TableCell sx={{ py: 0.5, fontWeight: 'bold' }}>RUC</TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontWeight: 'bold' }}>Acceso</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {empresasRrhh.map(e => (
                        <TableRow key={e.empresa_id} hover sx={{ height: 36 }}>
                          <TableCell sx={{ py: 0.5 }}>{e.razon_social}</TableCell>
                          <TableCell sx={{ py: 0.5, fontFamily: 'monospace', color: 'text.secondary' }}>{e.ruc}</TableCell>
                          <TableCell align="center" sx={{ py: 0.5 }}>
                            <Checkbox
                              checked={esAdmin || empresasRrhhUsuario.includes(e.empresa_id)}
                              disabled={esAdmin}
                              onChange={ev => handleEmpresaRrhhCheck(e.empresa_id, ev.target.checked)}
                              color="primary" size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {/* ── EMPRESAS CONTABLES (solo superadmin ve y gestiona) ── */}
            {esSuperAdmin && empresasContables.length > 0 && (
              <Paper sx={{ p: 3, borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <AccountBalanceIcon sx={{ color: '#1976d2' }} />
                  <Typography variant="h6" fontWeight="bold" sx={{ color: '#1976d2' }}>
                    Empresas Contables
                  </Typography>
                </Box>
                {esAdmin ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Este usuario es <strong>Administrador</strong> — tiene acceso a todas las empresas contables.
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Selecciona a qué empresas contables puede acceder este usuario.
                  </Typography>
                )}
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ py: 0.5, fontWeight: 'bold' }}>Empresa contable</TableCell>
                        <TableCell sx={{ py: 0.5, fontWeight: 'bold' }}>RUC</TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontWeight: 'bold' }}>Acceso</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {empresasContables.map(e => (
                        <TableRow key={e.id} hover sx={{ height: 36 }}>
                          <TableCell sx={{ py: 0.5 }}>{e.razon_social}</TableCell>
                          <TableCell sx={{ py: 0.5, fontFamily: 'monospace', color: 'text.secondary' }}>{e.ruc}</TableCell>
                          <TableCell align="center" sx={{ py: 0.5 }}>
                            <Checkbox
                              checked={esAdmin || empresasUsuario.includes(e.id)}
                              disabled={esAdmin}
                              onChange={ev => handleEmpresaContableCheck(e.id, ev.target.checked)}
                              color="primary"
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
